// PKT-0653 · one stat tile.
//
// LeadDetailModal and ClientRecordDialog each defined a local `stat(icon,
// label, value)` helper. Not similar — IDENTICAL: the same signature, the same
// sx block down to `alpha(theme.palette.primary.main, 0.04)`, the same
// noWrap/lineHeight on the value. The only difference was whitespace, one
// having been reformatted at some point and the other not.
//
// Two copies of a tile is how the two records start looking subtly unlike each
// other: a padding tweak lands in one, and the person's Budget renders one way
// before conversion and another way after, for no reason anybody chose.
//
// `noWrap` is deliberate and kept: these sit in a fixed-width row, and a wrapped
// value would push the tiles out of alignment. A value too long to fit is
// truncated with an ellipsis rather than silently reflowing the row.

import React from "react";
import { Box, Stack, Typography, alpha, useTheme } from "@mui/material";

export interface PersonStatProps {
  icon: React.ReactNode;
  label: string;
  /** Already formatted. "—" for unknown — never a zero standing in for one. */
  value: string;
  testId?: string;
}

export default function PersonStat({ icon, label, value, testId }: PersonStatProps) {
  const theme = useTheme();
  return (
    <Box
      data-testid={testId ?? `person-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      sx={{
        flex: 1,
        minWidth: 0,
        px: 1.25,
        py: 1,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
      }}
    >
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{ color: "text.secondary", mb: 0.25 }}
      >
        {icon}
        <Typography variant="caption" fontWeight={700}>
          {label}
        </Typography>
      </Stack>
      <Typography variant="subtitle2" fontWeight={800} noWrap sx={{ lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  );
}
